/**
 * The sales invoice editor.
 *
 * A thin shell over three tested modules, and deliberately so:
 *   - `invoiceFormState` decides what is valid and what gets sent.
 *   - `invoiceDraft` computes the live totals through the SAME engine the
 *     service uses, so the figure on screen is the figure that gets written.
 *   - `useInvoices` does the reading and the one write.
 *
 * There is no arithmetic in this file. A total computed here would be a second
 * calculation engine, which is the 1.6.x defect this project exists to kill
 * (ADR 0003) — and money formatting goes through core, never `toFixed`, because
 * ESLint bans it and because this total and the printed one must read alike.
 */
import { flushSync } from 'react-dom';
import { useMemo, useState, useRef } from 'react';
import { Plus, Trash2, Search, X } from 'lucide-react';
import { formatMoney } from '@core/money/format';
import { GST_STATES } from '@core/gst/stateCodes';
import { resolveInvoicePlaceOfSupply } from '@domain/placeOfSupply';
import { asMoney, rupeesText, type Quantity } from '@core/money';
import { fitScale } from '@core/doc/preview';
import { renderPreview } from '@core/doc/preview';
import type { NormalizedDocument } from '@core/doc/types';
import { assemble } from '@data/services/documentReader';
import type { Document, DocumentLine } from '@data/db/types';
import { toast } from '@ui/components/Toaster';
import { explainInvoiceError, computeInvoiceDraftTotals } from './invoiceDraft';
import {
  blankLine,
  emptyInvoiceForm,
  formFromDraft,
  formProblem,
  toDraftLines,
  toIssueInput,
  type DraftLine,
  type InvoiceFormState,
} from './invoiceFormState';
import { safeQuantity, formatQuantityDisplay } from './invoiceFormState';
import { loadBusinessIdentityWithImages } from '@data/services/businessReader';
import { getDb } from '@data/db/database';
import { DocumentScreen } from '@features/documents/DocumentScreen';
import {
  useCreateInvoice,
  useCurrentBusinessProfile,
  useEditableInvoice,
  useInvoiceOptions,
  useUpdateInvoiceDraft,
} from './useInvoices';
import type { ProductListItem } from '@data/services/productReader';
import type { Party } from '@data/db/types';
import { ITEM_OPTION_LIMIT, pickerOptions } from './itemPicker';
import { STANDARD_UNITS, DEFAULT_UNIT } from '@features/masterData/units';
import { financialYearFor } from '@domain/financialYear';

/**
 * Today, in the LOCAL calendar, as 'YYYY-MM-DD'.
 *
 * Local, not UTC: `toISOString()` would file an invoice raised at 00:30 IST
 * under the previous day, and it would then land in the previous financial year
 * with last year's invoice number. Built from the local parts and formatted
 * through the project's own `toIsoDate`, so there is exactly one date formatter.
 */
function todayIsoDate(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/** Where the shipped fonts are, as a URL the app can actually fetch. */
const ASSETS_BASE_URL = new URL('assets/', document.baseURI).href;

/** Monotonic-enough ids for React keys. Not persisted, so a counter is enough. */
let lineCounter = 0;
function nextLineId(): string {
  lineCounter += 1;
  return `line-${lineCounter}`;
}

/** Stable identities, so a `?? []` fallback cannot retrigger the memo below. */
const EMPTY_PRODUCTS: readonly ProductListItem[] = [];
const EMPTY_PARTIES: readonly Party[] = [];

const inputClass =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none';

/**
 * The place-of-supply control, and the only place a user answers the resolver.
 *
 * WHY IT IS HERE AND NOT ONLY IN THE SERVICE
 * -----------------------------------------
 * The locked rule is: when the place of supply cannot be determined, the issue is
 * BLOCKED and the user is asked to supply it. A block with no way to answer it is
 * a dead end - the document can never be issued, and the user has no idea what to
 * do about it. This control is the answer.
 *
 * It shows the RESOLVED value rather than a blank, because the common case is
 * "everything is fine and here is what we decided". An empty box on every invoice
 * would train the user to ignore the one time it matters.
 *
 * The choice is an OVERRIDE, not a re-specification: leaving it alone uses the
 * resolver's answer, and the label says which source produced it.
 */
function PlaceOfSupplyField({
  value,
  resolved,
  onChange,
}: {
  readonly value: string;
  readonly resolved: ReturnType<typeof resolveInvoicePlaceOfSupply> | null;
  readonly onChange: (code: string) => void;
}) {
  const unresolved = resolved !== null && !resolved.resolved;
  return (
    <div className="mt-3 rounded border border-slate-800 p-2">
      <label className="block text-xs uppercase tracking-wide text-slate-400" htmlFor="invoice-pos">
        Place of supply
      </label>
      <select
        id="invoice-pos"
        className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
        data-testid="invoice-place-of-supply"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {/*
          Empty = "use the resolver". Deliberately NOT a disabled placeholder:
          the resolved value is shown beside this, and forcing the user to
          re-pick a state on every invoice would train them to pick without
          reading, which is worse than showing the answer.
        */}
        <option value="">Use the resolved value</option>
        {GST_STATES.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name}
          </option>
        ))}
      </select>
      {unresolved ? (
        <p className="mt-1 text-xs text-amber-300" data-testid="invoice-pos-unresolved">
          Required before this invoice can be issued. Choose the state where the supply occurs.
        </p>
      ) : resolved ? (
        <p className="mt-1 text-xs text-slate-500" data-testid="invoice-pos-resolved">
          {resolved.source === 'OVERRIDE'
            ? 'Set by you'
            : resolved.source === 'DELIVERY'
              ? 'From the delivery address'
              : 'From the customer address'}
          {' Â· '}
          {GST_STATES.find((s) => s.code === resolved.code)?.name ?? resolved.code}
        </p>
      ) : null}
    </div>
  );
}

function Totals({ draft }: { draft: ReturnType<typeof computeInvoiceDraftTotals> }) {
  if (!draft) {
    return (
      <p className="text-sm text-slate-500">
        Add an item to see the total.
      </p>
    );
  }
  const rows: [string, number][] = [
    ['Taxable value', draft.taxablePaise],
    ...(draft.cgstPaise > 0 ? ([['CGST', draft.cgstPaise]] as [string, number][]) : []),
    ...(draft.sgstPaise > 0 ? ([['SGST', draft.sgstPaise]] as [string, number][]) : []),
    ...(draft.igstPaise > 0 ? ([['IGST', draft.igstPaise]] as [string, number][]) : []),
    ...(draft.cessPaise > 0 ? ([['Cess', draft.cessPaise]] as [string, number][]) : []),
  ];

  return (
    <div className="space-y-1 text-sm">
      {rows.map(([label, paise]) => (
        <div key={label} className="flex justify-between text-slate-300">
          <span>{label}</span>
          <span>{formatMoney(asMoney(paise))}</span>
        </div>
      ))}
      {draft.roundOffPaise !== 0 && (
        <div className="flex justify-between text-slate-300">
          <span>Round off</span>
          <span>{formatMoney(asMoney(draft.roundOffPaise))}</span>
        </div>
      )}
      <div className="flex justify-between border-t border-slate-700 pt-1 text-base font-semibold text-white">
        <span>Total</span>
        <span data-testid="invoice-total">{formatMoney(asMoney(draft.grandTotalPaise))}</span>
      </div>
      <p className="pt-1 text-xs text-slate-500">{draft.amountInWords}</p>
    </div>
  );
}

export function CreateInvoiceScreen({ documentId = null }: { documentId?: string | null }) {
  const options = useInvoiceOptions();
  const profile = useCurrentBusinessProfile();
  const create = useCreateInvoice();
  const updateDraft = useUpdateInvoiceDraft();
  const editable = useEditableInvoice(documentId);
  const editing = documentId !== null;

  const [state, setState] = useState<InvoiceFormState>(() => ({
    ...emptyInvoiceForm(nextLineId()),
    docDate: todayIsoDate(),
  }));

  // Hydrate the form from the stored draft ONCE, then leave it alone.
  //
  // The `loadedFor` marker is load-bearing, not tidiness. React Query re-renders
  // this component when `editable` settles, and the obvious guard -
  // `if (draft && !alreadyLoaded)` - would re-seed the form on every LATER
  // render too, wiping whatever the user had typed into any field, with no
  // error and no undo. Recording WHICH draft the form belongs to means it seeds
  // exactly once per draft, and never again.
  //
  // Assigning state during render (rather than in an effect) is deliberate and
  // is the supported React pattern for "reset state when an input changes":
  // React discards this render's output and immediately re-renders with the new
  // state, so the user never sees a frame of the empty form. An effect would
  // paint the empty form first, then overwrite it - a visible flash on every
  // draft open.
  //
  // `loadedFor` is STATE, not a ref, and that is the part the linter is right
  // about. A ref read and written during render is not a value React tracks, so
  // under StrictMode's double render the second pass would observe the marker
  // already set and skip the seed - a draft that opens EMPTY on the second
  // render pass, intermittently. State is part of the render output, so both
  // passes agree.
  // `?? []` inline would mint a NEW array on every render, so the useMemo below
  // would rebuild the lookup map each time and this screen would re-render its
  // whole item list on every keystroke. One empty array, stable identity.
  //
  // Declared ABOVE the `formFromDraft` call below, which needs the map to tell a
  // typed description from the product name. A `const` read before its
  // declaration is a runtime ReferenceError, not a lint warning.
  const products = options.data?.products ?? EMPTY_PRODUCTS;
  const customers = options.data?.customers ?? EMPTY_PARTIES;
  const productsById = useMemo(
    () => new Map(products.map((p) => [p.product.id, p])),
    [products],
  );

  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const storedDraft = editable.data;
  // Keyed on `docNumber`, not an id: `NormalizedDocument` is the PRINT shape
  // and deliberately carries no row id, and the number is the identity the user
  // actually recognises anyway.
  if (storedDraft && loadedFor !== storedDraft.docNumber) {
    setLoadedFor(storedDraft.docNumber);
    setState(formFromDraft(storedDraft, nextLineId, productsById));
  }

  // Global item search state (replaces per-line search)
  const [globalItemQuery, setGlobalItemQuery] = useState('');
  const globalShown = useMemo(
    () => pickerOptions(products, globalItemQuery, ''),
    [products, globalItemQuery],
  );

  const problem = formProblem(state, productsById);
  const customer = customers.find((c) => c.id === state.customerId) ?? null;

  // What the resolver makes of THIS transaction, with the user's override applied.
  //
  // Computed here so the screen can SHOW it, rather than letting the user save a
  // document whose place of supply nobody has looked at. It is the same function
  // the service calls with the same inputs, so the label on screen is the value
  // that will be written - not a parallel guess.
  const resolvedPos = profile.data
    ? resolveInvoicePlaceOfSupply({
        businessStateCode: profile.data.stateCode,
        customer,
        overrideStateCode: state.placeOfSupplyOverride,
      })
    : null;

  // Live totals, through the same engine the write path uses.
  //
  // `toDraftLines` carries the quantity and rate the user actually typed. An
  // earlier version of this file passed zeros here, which made the summary read
  // 0.00 for every invoice whatever was entered — a preview that is confidently
  // wrong is worse than none, because the user checks it before saving.
  // Compute draft for live preview even when form has validation errors (e.g., qty=0 during editing).
  // The Save button uses `formProblem` to block submission.
  const draft =
    profile.data
      ? computeInvoiceDraftTotals({
          business: profile.data,
          customer,
          docDate: state.docDate,
          lines: toDraftLines(state.lines).map((l, i) => ({
            ...l,
            id: state.lines[i]?.id ?? `line-${i}`,
          })),
          productsById,
          roundOff: state.roundOff,
          placeOfSupplyOverride: state.placeOfSupplyOverride,
        })
      : null;

  /**
   * Build a NormalizedDocument from the CURRENT FORM STATE for preview.
   *
   * This uses the same calculation engine as the live summary and the write path,
   * ensuring the preview reflects the CURRENT form state including any GST
   * overrides on individual lines. It does NOT reload from the database.
   */
  function buildPreviewDocument(): NormalizedDocument | null {
    if (!profile.data || !draft) return null;

    const business = profile.data;
    const customer = customers.find((c) => c.id === state.customerId) ?? null;

    // Build preview lines with all necessary data from the form state.
    // We filter to only lines with valid productId, and preserve the original
    // line's description, unit, and taxRateBp for the preview.
    const previewLines = state.lines
      .map((line, index) => ({
        ...toDraftLines([line])[0],
        id: line.id ?? `line-${index}`,
        description: line.description,
        unit: line.unit,
        productId: line.productId,
        originalIndex: index,
        discountType: line.discountType,
        discountValue: line.discountValue,
      }))
      .filter((l) => l.productId && productsById.has(l.productId));

    if (previewLines.length === 0) {
      return null;
    }

    // Build a minimal Document header for assemble()
    const header: Document = {
      id: 'preview',
      businessId: business.id,
      docType: 'SALES_INVOICE',
      docNumber: 'DRAFT',
      docNumberSeq: 0,
      financialYear: financialYearFor(state.docDate, business.fyStartMonth ?? 4),
      docDate: state.docDate,
      dueDate: undefined,
      expectedDate: undefined,
      expiryDate: undefined,
      customerId: state.customerId,
      supplierId: undefined,
      partySnapshot: customer
        ? {
            id: customer.id,
            name: customer.name,
            gstin: customer.gstin,
            stateCode: customer.stateCode ?? business.stateCode,
            stateName: customer.stateName ?? GST_STATES.find((s) => s.code === (customer.stateCode ?? business.stateCode))?.name ?? '',
            addressLines: customer.billingAddressLines ?? [],
            phone: customer.phone,
            email: customer.email,
          }
        : {
            id: '',
            name: 'Unknown Customer',
            gstin: undefined,
            stateCode: business.stateCode,
            stateName: GST_STATES.find((s) => s.code === business.stateCode)?.name ?? '',
            addressLines: [],
            phone: undefined,
            email: undefined,
          },
      deliveryAddressLines: undefined,
      deliveryStateCode: undefined,
      deliveryStateName: undefined,
      placeOfSupplyCode: draft.placeOfSupplyCode,
      supplyType: draft.supplyType,
      reverseCharge: false,
      quotationId: undefined,
      salesOrderId: undefined,
      purchaseOrderId: undefined,
      originalDocId: undefined,
      sourceKind: undefined,
      returnReasonCode: undefined,
      linkedChildDocIds: [],
      grossSubtotalPaise: draft.grossSubtotalPaise,
      lineDiscountPaise: draft.lineDiscountPaise,
      invoiceDiscountPaise: draft.invoiceDiscountPaise,
      totalDiscountPaise: draft.totalDiscountPaise,
      taxablePaise: draft.taxablePaise,
      taxableChargesPaise: draft.taxableChargesPaise,
      nonTaxableChargesPaise: draft.nonTaxableChargesPaise,
      cgstPaise: draft.cgstPaise,
      sgstPaise: draft.sgstPaise,
      igstPaise: draft.igstPaise,
      cessPaise: draft.cessPaise,
      tcsPaise: draft.tcsPaise,
      taxTotalPaise: draft.taxTotalPaise,
      preRoundOffPaise: draft.preRoundOffPaise,
      roundOffPaise: draft.roundOffPaise,
      grandTotalPaise: draft.grandTotalPaise,
      totalQtyMilli: draft.totalQty,
      totalFreeQtyMilli: draft.totalFreeQty,
      amountInWords: draft.amountInWords,
      charges: [],
      notes: undefined,
      terms: undefined,
      declaration: undefined,
      coverLetter: undefined,
      coverLetterEnabled: undefined,
      status: 'DRAFT',
      issuedAt: undefined,
      cancelledAt: undefined,
      cancelledBy: undefined,
      cancelReason: undefined,
      returnedAt: undefined,
      allocatedPaise: 0,
      balancePaise: draft.grandTotalPaise,
      calcVersion: draft.calcVersion,
      calcSnapshot: '',
      legacyData: undefined,
      createdAt: new Date().toISOString(),
      createdBy: business.id,
      updatedAt: new Date().toISOString(),
      updatedBy: business.id,
      syncVersion: 1,
    };

// Build DocumentLine array from preview lines matched with calculated draft totals
    const docLines: DocumentLine[] = previewLines.map((pl, index) => {
      // Find the corresponding draft line by originalIndex
      const dl = draft.lines.find((d) => d.lineId === `line-${pl.originalIndex}`) || draft.lines[index];
      const product = pl.productId ? productsById.get(pl.productId) : null;
      return {
        id: `preview-line-${index}`,
        businessId: business.id,
        documentId: 'preview',
        docType: 'SALES_INVOICE',
        lineNo: index + 1,
        productId: pl.productId,
        stockLocationId: undefined,
        description: pl.description || product?.product.name || 'Item',
        descriptionExtra: undefined,
        hsnSac: undefined,
        unit: pl.unit || product?.product.unit || 'NOS',
        productSnapshot: undefined,
        quantityMilli: dl?.quantity ?? 0,
        freeQuantityMilli: dl?.freeQuantity ?? 0,
        ratePaise: dl?.ratePaise ?? 0,
        discountType: 'NONE',
        discountValue: 0,
        discountPaise: 0,
        allocatedDocDiscountPaise: 0,
        taxablePaise: dl?.taxablePaise ?? 0,
        taxRateId: undefined,
        taxRateBp: dl?.taxRateBp ?? pl.taxRateBp ?? 0,
        cgstRateBp: dl?.cgstRateBp ?? 0,
        sgstRateBp: dl?.sgstRateBp ?? 0,
        igstRateBp: dl?.igstRateBp ?? 0,
        cgstPaise: dl?.cgstPaise ?? 0,
        sgstPaise: dl?.sgstPaise ?? 0,
        igstPaise: dl?.igstPaise ?? 0,
        cessRateBp: dl?.cessRateBp ?? 0,
        cessPaise: dl?.cessPaise ?? 0,
        totalPaise: dl?.totalPaise ?? 0,
        rateWithTaxPaise: dl?.rateWithTaxPaise ?? 0,
        fulfilledQtyMilli: 0,
        invoicedQtyMilli: 0,
        returnedQtyMilli: 0,
        incomeAccountId: undefined,
        expenseAccountId: undefined,
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: business.id,
        updatedAt: new Date().toISOString(),
        updatedBy: business.id,
        syncVersion: 1,
      };
    });

    // Use the existing assemble function to create NormalizedDocument
    return assemble(header, docLines, business.stateCode);
  }

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewScale] = useState(() => {
    const isMobile = window.innerWidth < 768;
    const availableWidth = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.55;
    return fitScale(availableWidth);
  });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const previewFrameRef = useRef<HTMLIFrameElement>(null);

  async function openPreview() {
    const doc = buildPreviewDocument();
    const business = profile.data;
    if (doc && business) {
      const biz = await loadBusinessIdentityWithImages(getDb() as never, business.id);
      const html = renderPreview(doc, biz, { scale: previewScale }, ASSETS_BASE_URL);
      setPreviewHtml(html);
      setPreviewOpen(true);
    }
  }

  function closePreview() {
    setPreviewOpen(false);
    setPreviewHtml(null);
  }

  function patchLine(id: string, next: Partial<DraftLine>) {
    setState((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.id === id ? { ...l, ...next } : l)),
    }));
  }

  /**
   * Find an existing line compatible with the given product, or return -1.
   * A compatible line has the same productId, unit, rate, and description.
   */
  function findCompatibleLineIndex(
    lines: readonly DraftLine[],
    productId: string,
    unit: string,
    rate: string,
    description: string,
  ): number {
    return lines.findIndex(
      (l) =>
        l.productId === productId &&
        l.unit === unit &&
        l.rate === rate &&
        l.description === description,
    );
  }

  function pickProduct(productId: string) {
    const product = productId ? productsById.get(productId) : undefined;
    if (!product) {
      flushSync(() => {
        setState((s) => ({
          ...s,
          lines: s.lines.map((l) => ({ ...l, productId: '' })),
        }));
      });
      setGlobalItemQuery('');
      return;
    }

    // Determine the values the new line would have
    const newUnit = product.product.unit || DEFAULT_UNIT;
    const newRate = rupeesText(asMoney(product.product.salePricePaise));
    const newDescription = '';
    // Get GST rate from product's taxRateBp (already resolved in productReader)
    const newTaxRateBp = product.taxRateBp ?? 0;

    flushSync(() => {
      setState((s) => {
      // Check if there's a compatible existing line to merge with
      const compatibleIndex = findCompatibleLineIndex(
        s.lines,
        productId,
        newUnit,
        newRate,
        newDescription,
      );

      if (compatibleIndex >= 0) {
        // Merge: increment quantity of existing compatible line
        const existingLine = s.lines[compatibleIndex];
        if (!existingLine) return s;
        const currentQtyMilli = safeQuantity(existingLine.quantity);
        return {
          ...s,
          lines: s.lines.map((l, i) =>
            i === compatibleIndex
              ? { ...l, quantity: formatQuantityDisplay((currentQtyMilli + 1000) as Quantity) }
              : l,
          ),
        };
      } else {
        // No compatible line: add new line (or replace the empty placeholder line)
        // Find the first line without a product, or add a new one
        const emptyLineIndex = s.lines.findIndex((l) => l.productId === '');
        if (emptyLineIndex >= 0) {
          // Replace the empty line
          const baseLine = s.lines[emptyLineIndex];
          if (!baseLine) return s;
          const newLine: DraftLine = {
            id: baseLine.id,
            productId,
            quantity: baseLine.quantity,
            rate: newRate,
            description: newDescription,
            unit: newUnit,
            taxRateBp: newTaxRateBp,
            discountType: 'NONE',
            discountValue: 0,
          };
          return {
            ...s,
            lines: s.lines.map((l, i) =>
              i === emptyLineIndex ? newLine : l,
            ),
          };
        } else {
          // Add new line
          const newLine: DraftLine = {
            id: nextLineId(),
            productId,
            quantity: '1',
            rate: newRate,
            description: newDescription,
            unit: newUnit,
            taxRateBp: newTaxRateBp,
            discountType: 'NONE',
            discountValue: 0,
          };
          return {
            ...s,
            lines: [...s.lines, newLine],
          };
}
        }
      });
    });

    setGlobalItemQuery('');
  }

  /**
 * Save the form, creating a NEW draft or replacing an EXISTING one.
 *
 * One function, two destinations, deliberately. The inputs come from the same
 * `toIssueInput` and are cleared by the same `formProblem` gate in both cases,
 * so the user cannot reach a state that is editable but unsavable, or the
 * reverse. Only the last hop differs.
 */
async function save() {
    const input = toIssueInput(state, productsById);
    if (!input) return;
    try {
      if (editing && documentId) {
        const out = await updateDraft.mutateAsync({
          ...input,
          id: documentId,
          placeOfSupplyOverride: state.placeOfSupplyOverride,
        });
        toast({
          title: `Invoice ${out.docNumber} updated`,
          description: 'Your changes are saved. Preview it, then issue when ready.',
          tone: 'success',
        });
        // Stay on the edit route rather than jumping to the document screen: the
        // user may have more changes to make, and bouncing out of the editor on
        // the first save is exactly what makes drafts feel un-editable.
        window.location.hash = `#/invoices/${out.id}/edit`;
        return;
      }
      const out = await create.mutateAsync({
      ...input,
      businessId: profile.data?.id,
      placeOfSupplyOverride: state.placeOfSupplyOverride,
    });
      toast({
        title: `Invoice ${out.docNumber} saved as draft`,
        description: 'Open it any time to keep editing, or issue it when ready.',
        tone: 'success',
      });
      // Straight into the EDITOR, not the document screen. Creation has always
      // produced a draft, and the old code navigated to a read-only screen -
      // which is why a freshly created invoice looked like it could not be
      // changed. Creating then editing is one continuous action.
      window.location.hash = `#/invoices/${out.id}/edit`;
    } catch (error) {
      toast({
        title: editing ? 'Could not update the invoice' : 'Could not save the invoice',
        description: explainInvoiceError(error),
        tone: 'danger',
      });
    }
  }

  if (editable.isLoading) {
    return <p className="p-4 text-sm text-slate-500">Loading the draft…</p>;
  }

  /*
   * An ISSUED or CANCELLED invoice opened on the edit route.
   *
   * The guard belongs HERE, before any editable UI renders. `useEditableInvoice`
   * returns null for these on purpose, and the form is still empty at this
   * point - so without this check the screen would show a blank, valid-looking
   * invoice editor for a document the user is NOT allowed to change. Saving it
   * would then be rejected by the service with a confusing error, after the user
   * had already typed. Showing the document screen instead is honest: it is
   * what the invoice is, and it carries the cancel action.
   *
   * It is also the backstop for the "Is this route even reachable?" question.
   * The list hides the Edit action on issued rows, but a stale bookmark or a
   * hand-typed URL can still get here, and this is where that stops.
   */
  if (editing && editable.isSuccess && storedDraft === null) {
    return <DocumentScreen documentId={documentId} />;
  }

  if (options.isLoading || profile.isLoading) {
    return <p className="p-4 text-sm text-slate-500">Loading…</p>;
  }

  return (
    <div className="flex flex-col p-4 text-slate-100">

      <div className="mb-3 flex items-center gap-3">
        <h1 className="text-lg font-semibold">
          {editing ? 'Edit sales invoice' : 'New sales invoice'}
        </h1>
        {editing && storedDraft ? (
          <span
            className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-300"
            data-testid="edit-draft-badge"
          >
            Draft {storedDraft.docNumber} — changes are saved, not issued
          </span>
        ) : (
          <span className="text-xs text-slate-500">
            Saving creates a draft. Issue it when the customer is ready.
          </span>
        )}
      </div>

      {customers.length === 0 || products.length === 0 ? (
        <p className="text-sm text-slate-400">
          {customers.length === 0
            ? 'No customers yet. Add one, or import a backup, before raising an invoice.'
            : 'No items in the catalogue yet. Add an item before raising an invoice.'}
        </p>
      ) : (
<div className="flex flex-col gap-4 lg:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-slate-400">
                  Customer
                  <select
                    className={inputClass}
                    data-testid="invoice-customer"
                    value={state.customerId}
                    onChange={(e) =>
                      setState((s) => ({ ...s, customerId: e.target.value }))
                    }
                  >
                    <option value="">Choose a customer…</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-slate-400">
                  Date
                  <input
                    type="date"
                    className={inputClass}
                    data-testid="invoice-date"
                    value={state.docDate}
                    onChange={(e) => setState((s) => ({ ...s, docDate: e.target.value }))}
                  />
                </label>
              </div>

              {/* Global Item Search */}
              <div className="rounded border border-slate-700 bg-slate-900/50 p-3">
                <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Search items
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" aria-hidden="true" />
                  <input
                    className={`${inputClass} pl-10`}
                    data-testid="invoice-global-item-search"
                    placeholder="Search items…"
                    value={globalItemQuery}
                    onChange={(e) => setGlobalItemQuery(e.target.value)}
                  />
                </div>
                {globalShown.truncated && (
                  <p className="mt-1 text-xs text-slate-500">
                    {globalItemQuery.trim() === ''
                      ? `Showing the first ${ITEM_OPTION_LIMIT} items. Search to narrow the list.`
                      : `${globalShown.matched} match, showing ${ITEM_OPTION_LIMIT}. Search to narrow.`}
                  </p>
                )}
              </div>

              {/* Invoice Lines Table - using CSS Grid for alignment */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-1 pr-2" style={{ width: '34%' }}>Item</th>
                      <th className="py-1 pr-2 text-center" style={{ width: '9%' }}>Qty</th>
                      <th className="py-1 pr-2 text-center" style={{ width: '11%' }}>Unit</th>
                      <th className="py-1 pr-2 text-right" style={{ width: '15%' }}>Rate</th>
                      <th className="py-1 pr-2 text-center" style={{ width: '10%' }}>GST</th>
                      <th className="py-1 pr-2 text-right" style={{ width: '19%' }}>Amount</th>
                      <th className="py-1" style={{ width: '2%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {state.lines.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-500">
                          No items yet. Search and select an item above.
                        </td>
                      </tr>
                    ) : (
                      state.lines.map((line, index) => {
                        const item = productsById.get(line.productId);
                        const isEmptyLine = line.productId === '';
                        // Get calculated line amount from draft (qty × rate)
                        const draftLine = draft?.lines[index];
                        const lineAmountPaise = draftLine?.grossPaise ?? 0;
                        return (
                          <tr key={line.id} className="border-b border-slate-800/60">
                            <td className="py-1 pr-2" style={{ width: '34%' }}>
                              {isEmptyLine ? (
                                <select
                                  className={inputClass}
                                  data-testid="invoice-item"
                                  value={line.productId}
                                  onChange={(e) => pickProduct(e.target.value)}
                                >
                                  <option value="">Choose an item…</option>
                                  {globalShown.options.map((p) => (
                                    <option key={p.product.id} value={p.product.id}>
                                      {p.product.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span
                                    className="font-medium truncate"
                                    data-testid="invoice-line-product-name"
                                  >
                                    {item?.product.name ?? 'Unknown item'}
                                  </span>
                                  <input
                                    className={`${inputClass} flex-1 mt-1 hidden sm:block`}
                                    data-testid="invoice-line-description"
                                    aria-label={`Description for line ${line.id}`}
                                    placeholder={item?.product.name ?? 'Description (optional)'}
                                    value={line.description}
                                    onChange={(e) =>
                                      patchLine(line.id, { description: e.target.value })
                                    }
                                  />
                                </div>
                              )}
                            </td>
                            <td className="py-1 pr-2 text-center" style={{ width: '9%' }}>
                              <input
                                className={`${inputClass} w-20 mx-auto`}
                                data-testid="invoice-qty"
                                inputMode="decimal"
                                value={line.quantity}
                                onChange={(e) => patchLine(line.id, { quantity: e.target.value })}
                              />
                            </td>
                            <td className="py-1 pr-2 text-center" style={{ width: '11%' }}>
                              <select
                                className={`${inputClass} w-24 mx-auto`}
                                data-testid="invoice-unit"
                                value={line.unit}
                                onChange={(e) => patchLine(line.id, { unit: e.target.value })}
                              >
                                {STANDARD_UNITS.map((u) => (
                                  <option key={u.code} value={u.code}>
                                    {u.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="py-1 pr-2 text-right" style={{ width: '15%' }}>
                              <input
                                className={`${inputClass} w-28 mx-auto text-right`}
                                data-testid="invoice-rate"
                                inputMode="decimal"
                                value={line.rate}
                                onChange={(e) => patchLine(line.id, { rate: e.target.value })}
                              />
                            </td>
                            <td className="py-1 pr-2 text-center" style={{ width: '10%' }}>
                              <select
                                className={`${inputClass} w-20 mx-auto`}
                                data-testid="invoice-gst"
                                value={line.taxRateBp ?? 0}
                                onChange={(e) => patchLine(line.id, { taxRateBp: Number(e.target.value) })}
                              >
                                <option value={0}>0%</option>
                                <option value={500}>5%</option>
                                <option value={1200}>12%</option>
                                <option value={1800}>18%</option>
                                <option value={2800}>28%</option>
                              </select>
                            </td>
                            <td className="py-1 pr-2 text-right text-slate-300 font-mono" style={{ width: '19%' }}>
                              {item ? formatMoney(asMoney(lineAmountPaise)) : '—'}
                            </td>
                            <td className="py-1 text-right" style={{ width: '2%' }}>
                              <button
                                type="button"
                                aria-label={`Remove line ${line.id}`}
                                className="text-slate-500 hover:text-red-400"
                                onClick={() =>
                                  setState((s) => ({ ...s, lines: s.lines.filter((l) => l.id !== line.id) }))
                                }
                              >
                                <Trash2 className="h-4 w-4" aria-hidden />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3">
              <button
                type="button"
                // Named for the same reason as every other control on this
                // screen: the workflow specs have to be able to add a second and
                // third line, and a multi-line invoice is where printing
                // actually breaks. Reaching for the button by its label would
                // couple the test to wording the UI is free to change.
                data-testid="invoice-add-item"
                className="flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-slate-500"
                onClick={() =>
                  setState((s) => ({ ...s, lines: [...s.lines, blankLine(nextLineId())] }))
                }
              >
                <Plus className="h-4 w-4" aria-hidden /> Add item
              </button>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={state.roundOff}
                  onChange={(e) => setState((s) => ({ ...s, roundOff: e.target.checked }))}
                />
                Round off total
              </label>
            </div>

            <PlaceOfSupplyField
              value={state.placeOfSupplyOverride}
              resolved={resolvedPos}
              onChange={(code) => setState((s) => ({ ...s, placeOfSupplyOverride: code }))}
            />
          </div>

          <aside className="w-full shrink-0 rounded-lg border border-slate-800 bg-slate-900/60 p-3 lg:w-72">
            <h2 className="mb-2 text-xs uppercase tracking-wide text-slate-400">Summary</h2>
            <Totals draft={draft} />
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                data-testid="invoice-save"
                // Both mutations gate the button. Using only `create` would leave
                // an edit double-submittable while `updateDraft` is in flight -
                // two writes of the same draft racing each other.
                disabled={problem !== null || create.isPending || updateDraft.isPending}
                className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
                onClick={save}
              >
                {updateDraft.isPending
                  ? 'Saving…'
                  : create.isPending
                    ? 'Creating…'
                    : editing
                      ? 'Save changes'
                      : 'Save draft'}
              </button>
              {editing && documentId && (
                <button
                  type="button"
                  data-testid="invoice-preview-link"
                  className="rounded border border-slate-700 px-3 py-2 text-center text-sm text-slate-300 hover:border-slate-500"
                  onClick={openPreview}
                >
                  Preview without saving
                </button>
              )}
              <a
                href="#/documents"
                className="rounded border border-slate-700 px-3 py-2 text-center text-sm text-slate-300 hover:border-slate-500"
              >
                {editing ? 'Back to invoices' : 'Cancel'}
              </a>
            </div>
            {problem !== null && (
              <p className="mt-2 text-xs text-amber-300" data-testid="invoice-problem">
                {problem}
              </p>
            )}
</aside>
        </div>
      )}

        {previewOpen && previewHtml && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={closePreview}
            role="dialog"
            aria-modal="true"
            aria-labelledby="preview-title"
          >
            <div
              className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 bg-slate-50">
                <h2 id="preview-title" className="text-lg font-semibold text-slate-900">
                  Invoice Preview
                </h2>
                <button
                  type="button"
                  onClick={closePreview}
                  className="text-slate-500 hover:text-slate-700"
                  aria-label="Close preview"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-3 sm:p-4 overflow-auto max-h-[calc(90vh-60px)]">
                <iframe
                  ref={previewFrameRef}
                  title="Invoice preview"
                  data-testid="invoice-preview"
                  srcDoc={previewHtml}
                  style={{
                    width: '100%',
                    height: '60vh',
                    maxHeight: '600px',
                    border: 0,
                    backgroundColor: 'white',
                  }}
                />
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
                <button
                  type="button"
                  onClick={closePreview}
                  className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {problem !== null && (
          <p className="mt-2 text-xs text-amber-300" data-testid="invoice-problem">
            {problem}
          </p>
        )}
    </div>
  );
}
